import { type UseFormReturn, useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { type AxiosError } from 'axios';
import { ROUTES } from '~/utils/routes';
import { CreateBranchFormFieldValues, createBranchSchema } from '../logics/schema';
import { CreateBranchFormPresentation } from '../presentations';
import { usePostBranch } from './usePostBranch';

type PresentationProps = React.ComponentProps<typeof CreateBranchFormPresentation>;

type IUseCreateBranchForm = () => {
  state: {
    methods: UseFormReturn<CreateBranchFormFieldValues>;
    isSubmitDisabled: PresentationProps['isSubmitDisabled'];
  };
  actions: {
    handleSubmit: PresentationProps['onSubmit'];
  };
};

export const useCreateBranchForm: IUseCreateBranchForm = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const methods = useForm<CreateBranchFormFieldValues>({
    defaultValues: {
      name: '',
      code: '',
    },
    criteriaMode: 'all',
    mode: 'onChange',
    resolver: zodResolver(createBranchSchema),
  });

  const threeLetterCode = getThreeLetterCode() || '';
  const { trigger, isMutating } = usePostBranch(threeLetterCode, {
    onSuccess: () => {
      showToast({
        message: '事業所を作成しました。',
        status: 'success',
      });
      navigate(ROUTES.BRANCHES.INDEX);
    },
    onError: (error: AxiosError<{ errors?: string[] }>) => {
      if (error.response?.status === 422) {
        const errors = error.response?.data?.errors;
        if (errors && errors.length > 0) {
          showToast({
            message: errors[0],
            status: 'danger',
          });
          return;
        }
      }

      showToast({
        message: '事業所の作成に失敗しました。',
        status: 'danger',
      });
    },
  });

  const handleSubmit: PresentationProps['onSubmit'] = methods.handleSubmit(data => {
    trigger({
      branch: {
        name: data.name,
        code: data.code,
      },
    });
  });

  return {
    state: {
      methods,
      isSubmitDisabled: !methods.formState.isValid || isMutating,
    },
    actions: {
      handleSubmit,
    },
  };
};
